import moment from 'moment-timezone'

/**
 * @param {string | null | undefined} hotelTimeZoneId
 * @returns {string | null}
 */
export function resolveHotelTimeZone(hotelTimeZoneId) {
  if (!hotelTimeZoneId || typeof hotelTimeZoneId !== 'string') {
    return null
  }
  const trimmed = hotelTimeZoneId.trim()
  if (!trimmed || !moment.tz.zone(trimmed)) {
    return null
  }
  return trimmed
}

/**
 * @param {string | null | undefined} hotelTimeZoneId
 * @returns {string | null}
 */
export function getHotelTimeZoneLabel(hotelTimeZoneId) {
  const zone = resolveHotelTimeZone(hotelTimeZoneId)
  if (!zone) {
    return null
  }
  return moment.tz(zone).zoneAbbr() || zone
}

/**
 * @param {string | null | undefined} hotelTimeZoneId
 * @returns {moment.Moment}
 */
export function getHotelNow(hotelTimeZoneId) {
  const zone = resolveHotelTimeZone(hotelTimeZoneId)
  return zone ? moment.tz(zone) : moment()
}

/**
 * @param {string | null | undefined} hotelTimeZoneId
 * @returns {string}
 */
export function getHotelTodayDateString(hotelTimeZoneId) {
  return getHotelNow(hotelTimeZoneId).format('YYYY-MM-DD')
}

/**
 * Renders a telemetry instant in the hotel's local wall-clock time when a valid IANA zone is supplied.
 * @param {string | null | undefined} value
 * @param {string | null | undefined} hotelTimeZoneId
 * @param {{ includeZoneLabel?: boolean }} [options]
 */
export function formatHotelTime(value, hotelTimeZoneId, options = {}) {
  if (value == null || value === '') {
    return '—'
  }

  const parsed = moment.utc(value)
  if (!parsed.isValid()) {
    return String(value)
  }

  const zone = resolveHotelTimeZone(hotelTimeZoneId)
  const formatted = zone
    ? parsed.tz(zone).format('MMM D, YYYY h:mm A')
    : parsed.local().format('MMM D, YYYY h:mm A')

  if (options.includeZoneLabel && zone) {
    const abbr = getHotelTimeZoneLabel(zone)
    return `${formatted} (${abbr} · hotel time)`
  }

  return formatted
}

/**
 * Calendar day boundaries for the timeline picker, anchored to the hotel time zone when available.
 * @param {string | null | undefined} fromDateStr
 * @param {string | null | undefined} toDateStr
 * @param {string | null | undefined} hotelTimeZoneId
 */
export function buildTimelineViewRangeFromCalendar(fromDateStr, toDateStr, hotelTimeZoneId) {
  const zone = resolveHotelTimeZone(hotelTimeZoneId)
  const now = getHotelNow(hotelTimeZoneId)
  const todayStr = now.format('YYYY-MM-DD')

  if (!fromDateStr || !toDateStr) {
    return { start: now.clone().subtract(24, 'hours'), end: now.clone() }
  }

  const fromDay = zone
    ? moment.tz(fromDateStr, 'YYYY-MM-DD', zone).startOf('day')
    : moment(fromDateStr).startOf('day')
  const toDay = zone
    ? moment.tz(toDateStr, 'YYYY-MM-DD', zone).endOf('day')
    : moment(toDateStr).endOf('day')

  if (!fromDay.isValid() || !toDay.isValid() || toDay.isBefore(fromDay)) {
    return { start: now.clone().subtract(24, 'hours'), end: now.clone() }
  }

  if (fromDateStr === toDateStr && fromDateStr === todayStr) {
    return { start: now.clone().subtract(24, 'hours'), end: now.clone() }
  }

  return {
    start: fromDay,
    end: moment.min(toDay, now.clone())
  }
}

/**
 * Formats a timeline boundary label in hotel local time.
 * @param {moment.Moment} value
 * @param {string | null | undefined} hotelTimeZoneId
 */
export function formatTimelineBoundaryLabel(value, hotelTimeZoneId) {
  if (!value || !value.isValid()) {
    return '—'
  }

  const zone = resolveHotelTimeZone(hotelTimeZoneId)
  if (zone) {
    return value.clone().tz(zone).format('MMM D, h:mm A')
  }

  return value.clone().local().format('MMM D, h:mm A')
}
